/**
 * @license
 * Copyright 2023 Google LLC.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * =============================================================================
 */
import * as posedetection from '@tensorflow-models/pose-detection';
import * as scatter from 'scatter-gl';

import * as params from './params';

// These anchor points allow the pose pointcloud to resize according to its
// position in the input.
const ANCHOR_POINTS = [[0, 0, 0], [0, 1, 0], [-1, 0, 0], [-1, -1, 0]];

export class RendererCanvas2d {
  constructor(canvas) {
    this.ctx = canvas.getContext('2d');
    // this.scatterGLEl = document.querySelector('#scatter-gl-container');
    // this.scatterGL = new scatter.ScatterGL(this.scatterGLEl, {
    //   'rotateOnStart': true,
    //   'selectEnabled': false,
    //   'styles': {polyline: {defaultOpacity: 1, deselectedOpacity: 1}}
    // });
    // this.scatterGLHasInitialized = false;
    // this.videoWidth = canvas.width;
    // this.videoHeight = canvas.height;
    // // this.flip(this.videoWidth, this.videoHeight);
    // this.scatterGLEl.style.display =
    //     params.STATE.modelConfig.render3D ? 'inline-block' : 'none';
  }

  flip(videoWidth, videoHeight) {
    // Because the image from camera is mirrored, need to flip horizontally.
    this.ctx.translate(videoWidth, 0);
    this.ctx.scale(-1, 1);

    this.scatterGLEl.style =
        `width: ${videoWidth}px; height: ${videoHeight}px;`;
    this.scatterGL.resize();

    this.scatterGLEl.style.display =
        params.STATE.modelConfig.render3D ? 'inline-block' : 'none';
  }

  draw(rendererParams) {
    const [video, poses, isModelChanged, zoomLevel] = rendererParams;
    if (video.complete) {
      // The image is already loaded, trigger the load event manually
      this.drawCtx(video);
    } else {
      // Wait for the image to load
      video.addEventListener("load",this.drawCtx(video));
    }
    

    // The null check makes sure the UI is not in the middle of changing to a
    // different model. If during model change, the result is from an old model,
    // which shouldn't be rendered.
    if (poses && poses.length > 0 && !isModelChanged) {
      this.drawResults(poses, zoomLevel);
    }
  }

  drawCtx(video) {
    this.ctx.drawImage(video, 0, 0, this.videoWidth, this.videoHeight);
  }

  clearCtx() {
    this.ctx.clearRect(0, 0, this.videoWidth, this.videoHeight);
  }

  /**
   * Draw the keypoints and skeleton on the video.
   * @param poses A list of poses to render.
   */
  drawResults(poses, zoomLevel = 1) {
    for (const pose of poses) {
      this.drawResult(pose, zoomLevel);
    }
  }

  /**
   * Draw the keypoints and skeleton on the video.
   * @param pose A pose with keypoints to render.
   */
  drawResult(pose, zoomLevel = 1) {
    if (pose.keypoints !== null) {
      this.drawKeypoints(pose.keypoints);
      this.drawSkeleton(pose.keypoints, pose.id);
    }
    if (pose.keypoints3D !== null && params.STATE.modelConfig.render3D) {
      this.drawKeypoints3D(pose.keypoints3D);
    }
  }

  /**
   * Draw the keypoints on the video.
   * @param keypoints A list of keypoints.
   */
  drawKeypoints(keypoints) {
    const keypointInd =
        posedetection.util.getKeypointIndexBySide(params.STATE.model);
    this.ctx.fillStyle = 'Red';
    this.ctx.strokeStyle = 'White';
    this.ctx.lineWidth = params.DEFAULT_LINE_WIDTH;

    for (const i of keypointInd.middle) {
      this.drawKeypoint(keypoints[i]);
    }

    this.ctx.fillStyle = 'Green';
    for (const i of keypointInd.left) {
      this.drawKeypoint(keypoints[i]);
    }

    this.ctx.fillStyle = 'Orange';
    for (const i of keypointInd.right) {
      this.drawKeypoint(keypoints[i]);
    }
  }

  drawKeypoint(keypoint, size = 1) {
    // If score is null, just show the keypoint.
    const score = keypoint.score !== null ? keypoint.score : 1;
    const scoreThreshold = params.STATE.modelConfig.scoreThreshold || 0;

    if (score >= scoreThreshold) {

      const scale = this.ctx.getTransform().a; // Get the current scale factor

      // Calculate the adjusted radius based on the current scale and canvas-to-display ratio
      const canvasToDisplayRatio = this.ctx.canvas.width / this.ctx.canvas.offsetWidth;
      const adjustedRadius = (params.DEFAULT_RADIUS * canvasToDisplayRatio) / scale;

      const circle = new Path2D();
      this.ctx.fillStyle = "White";
      circle.arc(keypoint.x, keypoint.y, adjustedRadius * size, 0, 2 * Math.PI);
      this.ctx.fill(circle);
      this.ctx.stroke(circle);
    }
  }

  drawKeypointCustom(keypoint, size = 0.625) {
    // If score is null, just show the keypoint.
    const score = keypoint.score !== null ? keypoint.score : 1;
    const scoreThreshold = params.STATE.modelConfig.scoreThreshold || 0;

    if (score >= scoreThreshold) {

      const scale = this.ctx.getTransform().a; // Get the current scale factor

      // Calculate the adjusted radius based on the current scale and canvas-to-display ratio
      const canvasToDisplayRatio = this.ctx.canvas.width / this.ctx.canvas.offsetWidth;
      const adjustedRadius = (params.DEFAULT_RADIUS * canvasToDisplayRatio) / scale;

      const circle = new Path2D();
      this.ctx.fillStyle = "#59d500";
      // this.ctx.fillStyle = "White";
      circle.arc(keypoint.x, keypoint.y, adjustedRadius * size, 0, 2 * Math.PI);
      this.ctx.fill(circle);
      this.ctx.stroke(circle);
    }
  }

  /**
   * Draw the skeleton of a body on the video.
   * @param keypoints A list of keypoints.
   */
  drawSkeleton(keypoints, poseId) {
    // Each poseId is mapped to a color in the color palette.
    // const color = params.STATE.modelConfig.enableTracking && poseId !== null ?
    //     COLOR_PALETTE[poseId % 20] :
    //     'White';
    const color = "white";
    this.ctx.fillStyle = color;
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = params.DEFAULT_LINE_WIDTH;

    posedetection.util.getAdjacentPairs(params.STATE.model).forEach(([
                                                                      i, j
                                                                    ]) => {
      const kp1 = keypoints[i];
      const kp2 = keypoints[j];

      // If score is null, just show the keypoint.
      const score1 = kp1.score !== null ? kp1.score : 1;
      const score2 = kp2.score !== null ? kp2.score : 1;
      const scoreThreshold = params.STATE.modelConfig.scoreThreshold || 0;

      if (score1 >= scoreThreshold && score2 >= scoreThreshold) {
        this.ctx.beginPath();
        this.ctx.moveTo(kp1.x, kp1.y);
        this.ctx.lineTo(kp2.x, kp2.y);
        this.ctx.stroke();
      }
    });
  }

  drawKeypoints3D(keypoints) {
    const scoreThreshold = params.STATE.modelConfig.scoreThreshold || 0;
    const pointsData =
        keypoints.map(keypoint => ([-keypoint.x, -keypoint.y, -keypoint.z]));

    const dataset =
        new scatter.ScatterGL.Dataset([...pointsData, ...ANCHOR_POINTS]);

    const keypointInd =
        posedetection.util.getKeypointIndexBySide(params.STATE.model);
    this.scatterGL.setPointColorer((i) => {
      if (keypoints[i] === null || keypoints[i] === undefined || keypoints[i].score < scoreThreshold) {
        // hide anchor points and low-confident points.
        return '#ffffff';
      }
      if (i === 0) {
        return '#ff0000' /* Red */;
      }
      if (keypointInd.left.indexOf(i) > -1) {
        return '#00ff00' /* Green */;
      }
      if (keypointInd.right.indexOf(i) > -1) {
        return '#ffa500' /* Orange */;
      }
    });

    if (!this.scatterGLHasInitialized) {
      this.scatterGL.render(dataset);
    } else {
      this.scatterGL.updateDataset(dataset);
    }
    const connections = posedetection.util.getAdjacentPairs(params.STATE.model);
    const sequences = connections.map(pair => ({indices: pair}));
    this.scatterGL.setSequences(sequences);
    this.scatterGLHasInitialized = true;
  }

  drawRotatedLabelAtCenter(x1, y1, x2, y2, label) {
    // Calculate the center position between two pointers
    const centerX = (x1 + x2) / 2;
    const centerY = (y1 + y2) / 2;
  
    // Calculate the angle between the two pointers
    const angle = Math.atan2(y2 - y1, x2 - x1);
  
    // Calculate canvas-to-display ratio for consistent text size
    const canvasToDisplayRatio = this.ctx.canvas.width / this.ctx.canvas.offsetWidth;
    
    // Set the style for the label - scale font size based on canvas resolution
    this.ctx.fillStyle = "white";
    this.ctx.font = `${Math.round(14 * canvasToDisplayRatio)}px Arial`;  // Scale font size
    this.ctx.textAlign = "center";
  
    // Save the current transformation matrix
    this.ctx.save();
  
    // Translate to the center position
    this.ctx.translate(centerX, centerY);
  
    // Rotate the label based on the calculated angle
    this.ctx.rotate(angle);
  
    // Draw the rotated label
    this.ctx.fillText(label, 0, 0);
  
    // Restore the original transformation matrix
    this.ctx.restore();
  }

  drawNewSkeleton(pointPair, distance) {
    this.ctx.fillStyle = "white";
    this.ctx.strokeStyle = "red";
    this.ctx.beginPath();
    this.ctx.moveTo(pointPair[0].x, pointPair[0].y);
    this.ctx.lineTo(pointPair[1].x, pointPair[1].y);
    this.ctx.stroke();

    // this.drawLabel(distance, pointPair[0], pointPair[1], "center", 3 )
    this.ctx.strokeStyle = "#59d500";
    this.drawRotatedLabelAtCenter(pointPair[0].x, pointPair[0].y , pointPair[1].x, pointPair[1].y, distance )
  }

  setZoomLevel(zoom) {
    this.zoomLevel = zoom;
  }
}
